import React, { useState } from 'react';
import { patchRegistrationStatus, unregisterParticipant } from '../api/eventsApi';
import { updateMemberAlias } from '../api/membersApi';
import { getMemberDisplay, MemberIdentity } from './memberDisplay';
import { useToast } from './Toast';

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconNotAttended = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
  </svg>
);

const IconAttended = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </svg>
);

// NOT_CHARGED — dollar sign (gray)
const IconNotCharged = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const IconTrash = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

// Иконка «присвоить лейбл» — ценник/тег
const IconTag = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const openTelegramChat = (username) => {
  if (!username) return;
  if (window.Telegram?.WebApp?.openTelegramLink) {
    window.Telegram.WebApp.openTelegramLink(`https://t.me/${username}`);
  }
};

// ─── ChargeSource cycle ───────────────────────────────────────────────────────
const CHARGE_CYCLE = ['NOT_CHARGED', 'PAYMENT', 'SUBSCRIPTION'];

const ChargeButton = ({ source, loading, onClick }) => {
  if (source === 'PAYMENT') {
    return (
      <button
        onClick={onClick}
        disabled={loading}
        title="Оплатил (нажмите для смены)"
        style={{
          background: '#fefce8',
          border: '1.5px solid #e5e7eb',
          borderRadius: 8, padding: '5px 7px',
          cursor: loading ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: loading ? 0.5 : 1,
          transition: 'all 0.15s',
          minWidth: 36, minHeight: 36,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="9" y1="1" x2="9" y2="23" />
          <path d="M14 5H6.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H3" />
          <polyline points="16 11 18 13 22 9" />
        </svg>
      </button>
    );
  }

  if (source === 'SUBSCRIPTION') {
    return (
      <button
        onClick={onClick}
        disabled={loading}
        title="Абонемент (нажмите для смены)"
        style={{
          background: '#bbf7d0',
          border: '1.5px solid #4ade80',
          borderRadius: 8, padding: '5px 7px',
          cursor: loading ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: loading ? 0.5 : 1,
          transition: 'all 0.15s',
          minWidth: 36, minHeight: 36,
          fontWeight: 800, fontSize: 15, color: '#15803d',
          fontFamily: 'sans-serif',
          lineHeight: 1,
        }}
      >
        А
      </button>
    );
  }

  // NOT_CHARGED
  return (
    <button
      onClick={onClick}
      disabled={loading}
      title="Не оплачено (нажмите для смены)"
      style={{
        background: '#f9fafb',
        border: '1.5px solid #e5e7eb',
        borderRadius: 8, padding: '5px 7px',
        cursor: loading ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: loading ? 0.5 : 1,
        transition: 'all 0.15s',
        minWidth: 36, minHeight: 36,
      }}
    >
      <IconNotCharged />
    </button>
  );
};

const chargeLabel = (source) => {
  if (source === 'PAYMENT') return 'Оплатил';
  if (source === 'SUBSCRIPTION') return 'Абонемент';
  return 'Не оплачено';
};

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
const ConfirmDialog = ({ name, onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  }}>
    <div style={{
      background: '#fff', borderRadius: 16, padding: '24px 20px',
      maxWidth: 320, width: '100%',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      animation: 'popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: '#fee2e2',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 14px',
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#111827', textAlign: 'center' }}>
        Удалить участника?
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.5 }}>
        <strong style={{ color: '#374151' }}>{name}</strong> будет снят с регистрации. Это действие нельзя отменить.
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel} style={{
          flex: 1, padding: '10px', borderRadius: 10,
          border: '1.5px solid #e5e7eb', background: '#f9fafb',
          fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer',
        }}>
          Отмена
        </button>
        <button onClick={onConfirm} style={{
          flex: 1, padding: '10px', borderRadius: 10,
          border: 'none', background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
        }}>
          Удалить
        </button>
      </div>
    </div>
    <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const RegistrationRow = ({ registration, index, canManage, editMode, eventId, onDeleted, onChanged }) => {
  const { addToast } = useToast();

  const [attended, setAttended] = useState(registration.attended ?? false);
  const [chargeSource, setChargeSource] = useState(registration.chargeSource || 'NOT_CHARGED');
  const [loadingAttended, setLoadingAttended] = useState(false);
  const [loadingCharge, setLoadingCharge] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingAlias, setEditingAlias] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');
  const [savingAlias, setSavingAlias] = useState(false);

  const isWaiting = registration.status === 'WAITING';

  // Единый слой отображения имени/ника/алиаса (гость vs реальный) — см. memberDisplay.jsx.
  // Гость: имя серым чипом + @pendingUsername в сером боксе; реальный: имя/синий @username + alias-чип.
  const view = getMemberDisplay(registration);
  const hasUser = !view.isGuest;
  const displayName = view.name;   // используется в тостах и диалоге удаления
  const tgUsername = view.nick;    // @ник: реальный → telegramUserName, гость → pendingUsername

  const startEditAlias = () => {
    setAliasDraft(registration.alias || '');
    setEditingAlias(true);
  };

  const cancelAlias = () => {
    setEditingAlias(false);
    setAliasDraft('');
  };

  const saveAlias = async () => {
    if (savingAlias) return;
    setSavingAlias(true);
    try {
      await updateMemberAlias(registration.organizationMemberId, aliasDraft.trim(), eventId);
      addToast('Имя обновлено', 'success');
      setEditingAlias(false);
      (onChanged || onDeleted)?.();
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setSavingAlias(false);
    }
  };

  const toggleAttended = async () => {
    if (loadingAttended) return;
    const newValue = !attended;
    setLoadingAttended(true);
    try {
      await patchRegistrationStatus(registration.registrationId, { attended: newValue });
      setAttended(newValue);
      addToast(
        newValue ? `${displayName} отмечен(а) как пришедший` : `${displayName} отмечен(а) как отсутствующий`,
        'success'
      );
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setLoadingAttended(false);
    }
  };

  const cycleCharge = async () => {
    if (loadingCharge) return;
    const currentIdx = CHARGE_CYCLE.indexOf(chargeSource);
    const nextSource = CHARGE_CYCLE[(currentIdx + 1) % CHARGE_CYCLE.length];
    setLoadingCharge(true);
    try {
      await patchRegistrationStatus(registration.registrationId, { chargeSource: nextSource });
      setChargeSource(nextSource);
      addToast(`Статус оплаты: ${chargeLabel(nextSource)}`, 'success');
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setLoadingCharge(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setShowConfirm(false);
    try {
      await unregisterParticipant(eventId, { registrationId: registration.registrationId });
      addToast(`${displayName} снят с регистрации`, 'success');
      onDeleted?.();
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
      setDeleting(false);
    }
  };

  return (
    <>
      {showConfirm && (
        <ConfirmDialog
          name={displayName}
          onConfirm={handleDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 6px',
        background: deleting ? '#fef2f2' : isWaiting ? '#fffbeb' : '#fff',
        borderRadius: 12,
        border: `1.5px solid ${deleting ? '#fca5a5' : isWaiting ? '#fde68a' : '#f3f4f6'}`,
        transition: 'all 0.15s',
        opacity: deleting ? 0.6 : 1,
      }}>
        {/* Index — для подтверждённых компактный номер без кружка (экономим место
            под имя), для листа ожидания оставляем небольшой бейдж с часами */}
        {isWaiting ? (
          <div style={{
            width: 20, height: 20, borderRadius: '50%',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
        ) : (
          <div style={{
            minWidth: 16, textAlign: 'center',
            color: '#9ca3af', fontSize: 12, fontWeight: 700,
            flexShrink: 0,
          }}>
            {index + 1}
          </div>
        )}

        {/* Name + username + alias-пометка. Inline-правка alias в режиме редактирования. */}
        {editingAlias ? (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              autoFocus
              value={aliasDraft}
              onChange={e => setAliasDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveAlias(); if (e.key === 'Escape') cancelAlias(); }}
              placeholder={hasUser ? 'Пометка...' : 'Имя участника...'}
              style={{
                flex: 1, minWidth: 0, boxSizing: 'border-box',
                padding: '6px 8px', borderRadius: 8, border: '1.5px solid #6366f1',
                fontSize: 14, color: '#111827', outline: 'none',
              }}
            />
            <button
              onClick={saveAlias}
              disabled={savingAlias}
              title="Сохранить"
              style={{
                background: '#dcfce7', border: '1.5px solid #86efac', borderRadius: 8,
                padding: '5px 7px', cursor: savingAlias ? 'wait' : 'pointer', color: '#16a34a',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <IconCheck />
            </button>
            <button
              onClick={cancelAlias}
              disabled={savingAlias}
              title="Отмена"
              style={{
                background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 8,
                padding: '5px 7px', cursor: 'pointer', color: '#6b7280',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <IconX />
            </button>
          </div>
        ) : (
          <MemberIdentity
            view={view}
            size={32}
            style={{ flex: 1, gap: 8 }}
            onNickClick={hasUser && tgUsername ? () => openTelegramChat(tgUsername) : undefined}
          />
        )}

        {/* Admin controls (non-edit mode) */}
        {canManage && !editMode && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              onClick={toggleAttended}
              disabled={loadingAttended}
              title={attended ? 'Пришёл (нажмите чтобы отменить)' : 'Отметить как пришедшего'}
              style={{
                background: attended ? '#dcfce7' : '#f9fafb',
                border: `1.5px solid ${attended ? '#86efac' : '#e5e7eb'}`,
                borderRadius: 8, padding: '5px 7px',
                cursor: loadingAttended ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: loadingAttended ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              {attended ? <IconAttended /> : <IconNotAttended />}
            </button>

            <ChargeButton
              source={chargeSource}
              loading={loadingCharge}
              onClick={cycleCharge}
            />
          </div>
        )}

        {/* Edit mode — изменить имя/пометку + снять с регистрации */}
        {canManage && editMode && !editingAlias && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {/* Кнопка лейбла — только для реальных юзеров и гостей с pendingUsername.
                Гость без pendingUsername изменяется глобально, не в рамках события. */}
            {(!view.isGuest || view.awaiting) && (
            <button
              onClick={startEditAlias}
              title={view.isGuest ? 'Задать имя участнику' : 'Изменить пометку'}
              style={{
                background: '#eef2ff',
                border: '1.5px solid #c7d2fe',
                borderRadius: 8, padding: '5px 7px',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#6366f1',
                transition: 'all 0.15s',
              }}
            >
              <IconTag />
            </button>
            )}
            <button
              onClick={() => setShowConfirm(true)}
              disabled={deleting}
              title="Снять с регистрации"
              style={{
                background: '#fee2e2',
                border: '1.5px solid #fca5a5',
                borderRadius: 8, padding: '5px 7px',
                cursor: deleting ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#dc2626',
                opacity: deleting ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              <IconTrash />
            </button>
          </div>
        )}

        {/* Read-only badges for non-admin */}
        {!canManage && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {isWaiting && (
              <span style={{ fontSize: 11, fontWeight: 600, color: '#b45309', background: '#fef3c7', borderRadius: 6, padding: '2px 8px', border: '1px solid #fde68a' }}>
                В очереди
              </span>
            )}
            {attended && !isWaiting && (
              <span style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#dcfce7', borderRadius: 6, padding: '2px 8px' }}>
                Пришёл
              </span>
            )}
          </div>
        )}

      </div>
    </>
  );
};

export default RegistrationRow;